import './instrument';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { StyledEngineProvider } from '@mui/material/styles';
import GlobalStyles from '@mui/material/GlobalStyles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';

import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { msalConfig } from './sharedModules/utils/authConfig';

export const msalInstance = new PublicClientApplication(msalConfig);

msalInstance.initialize().then(() => {
  // Default to using the first account if no account is active on page load
  if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
    // Account selection logic is app dependent. Adjust as needed for different use cases.
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
  }

  // Optional - This will update account state if a user signs in from another tab or window
  msalInstance.enableAccountStorageEvents();

  msalInstance.addEventCallback((event: any) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
      const account = event.payload.account;
      msalInstance.setActiveAccount(account);
    }
  });

  // When loginPopup redirects back to /auth, the parent window reads the auth response from this
  // window's URL hash. Rendering the app here would fire API calls whose 401 handler navigates away
  // and drops that hash, so leave the popup blank until the parent closes it.
  if (window.opener && window.location.pathname === '/auth') {
    return;
  }

  const container = document.getElementById('root');
  if (!container) {
    throw new Error('Root element #root not found');
  }

  const root = createRoot(container);
  const queryClient = new QueryClient();

  root.render(
    <Sentry.ErrorBoundary fallback={<p>Something went wrong. Please refresh the page.</p>}>
      <StyledEngineProvider injectFirst>
        <QueryClientProvider client={queryClient}>
          <GlobalStyles styles="@layer theme, base, mui, components, utilities;" />
          <App msalInstance={msalInstance} />
        </QueryClientProvider>
      </StyledEngineProvider>
    </Sentry.ErrorBoundary>,
  );
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
